/**
 * Manda para o backend (`POST /v1/alertas/cliente`) um erro que só o NAVEGADOR
 * vê: tela que quebrou, exceção solta, rede caída e 502/503/504 do gateway —
 * esses nunca chegam ao Nest, então o filtro de exceções de lá não os conhece.
 *
 * ⚠️ Três travas, todas para o reporter não virar o problema:
 *
 *   1. `fetch` DIRETO, nunca o `httpClient`: se o reporte falhar, o
 *      `httpClient` tentaria reportar a própria falha, em laço.
 *   2. Deduplica por sessão: o mesmo erro (tipo + mensagem + status + rota)
 *      sai uma vez só. Um componente quebrando a cada render não vira enxurrada.
 *   3. No máximo `MAX_POR_MINUTO` envios por minuto, no total.
 *
 * Nunca lança e nunca espera: quem chama já está tratando um erro.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
const CAMINHO = '/v1/alertas/cliente';
const MAX_POR_MINUTO = 10;
const MAX_MENSAGEM = 1_000;
const MAX_STACK = 4_000;
const MAX_RESPOSTA = 3_500;

export type TipoDeErroDoCliente = 'render' | 'script' | 'promessa' | 'http' | 'rede';

export interface ErroDoCliente {
  tipo: TipoDeErroDoCliente;
  mensagem: string;
  metodo?: string;
  /** Rota da API chamada, quando o erro foi numa chamada. */
  rotaDaApi?: string;
  statusHttp?: number;
  /** Corpo de erro que a API (ou o gateway) devolveu. Nunca o corpo ENVIADO. */
  resposta?: unknown;
  stack?: string;
}

const jaReportados = new Set<string>();
let envios: number[] = [];

/** Só para os testes: zera a deduplicação e o limite. */
export function reiniciarReporterParaTestes(): void {
  jaReportados.clear();
  envios = [];
}

function cortar(texto: string | undefined, maximo: number): string | undefined {
  if (!texto) return undefined;
  return texto.length > maximo ? texto.slice(0, maximo) : texto;
}

/** A resposta vai junto só se couber — o backend recusa acima de 4 KB. */
function respostaQueCabe(resposta: unknown): unknown {
  if (resposta === undefined) return undefined;
  if (typeof resposta === 'string') return cortar(resposta, MAX_RESPOSTA);
  try {
    const json = JSON.stringify(resposta);
    return json && json.length <= MAX_RESPOSTA ? resposta : cortar(json, MAX_RESPOSTA);
  } catch {
    return undefined;
  }
}

export function reportarErro(erro: ErroDoCliente): void {
  try {
    // Sem internet do lado da PESSOA não é problema do sistema.
    if (erro.tipo === 'rede' && typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const chave = [erro.tipo, erro.mensagem, erro.statusHttp ?? '', erro.rotaDaApi ?? ''].join('|');
    if (jaReportados.has(chave)) return;

    const agora = Date.now();
    envios = envios.filter((momento) => agora - momento < 60_000);
    if (envios.length >= MAX_POR_MINUTO) return;

    jaReportados.add(chave);
    envios.push(agora);

    const corpo = {
      tipo: erro.tipo,
      mensagem: cortar(erro.mensagem.trim() || 'Erro sem mensagem', MAX_MENSAGEM),
      tela: cortar(window.location.pathname, 500),
      ...(erro.metodo ? { metodo: erro.metodo.toUpperCase() } : {}),
      ...(erro.rotaDaApi ? { rotaDaApi: cortar(erro.rotaDaApi.split('?')[0], 500) } : {}),
      ...(erro.statusHttp !== undefined ? { statusHttp: erro.statusHttp } : {}),
      ...(erro.resposta !== undefined ? { resposta: respostaQueCabe(erro.resposta) } : {}),
      ...(erro.stack ? { stack: cortar(erro.stack, MAX_STACK) } : {}),
    };

    void fetch(`${BASE}${CAMINHO}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(corpo),
      // Sobrevive à navegação: o erro que derruba a tela costuma vir junto de
      // um reload ou de um redirect.
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reportar é melhor-esforço: nada aqui pode virar um segundo erro.
  }
}

/** `window.onerror` e `unhandledrejection` — chamado uma vez no `main.tsx`. */
export function registrarCapturaGlobalDeErros(): void {
  window.addEventListener('error', (evento) => {
    const erro = evento.error as unknown;
    reportarErro({
      tipo: 'script',
      mensagem: erro instanceof Error ? erro.message : evento.message || 'Erro de script',
      ...(erro instanceof Error && erro.stack ? { stack: erro.stack } : {}),
    });
  });

  window.addEventListener('unhandledrejection', (evento) => {
    const motivo = evento.reason as unknown;
    reportarErro({
      tipo: 'promessa',
      mensagem: motivo instanceof Error ? motivo.message : String(motivo),
      ...(motivo instanceof Error && motivo.stack ? { stack: motivo.stack } : {}),
    });
  });
}
